import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { FileUpload } from '@/components/ui/FileUpload';
import { Modal } from '@/components/ui/Modal';
import { Textarea } from '@/components/ui/Textarea';
import { HAND_IN_ACCEPT, HAND_IN_MAX_MB, type StudentWorkItem } from './workItem';

/** Mirrors `submitHomeworkSchema.notes` on the server. */
const NOTE_MAX = 500;

interface HomeworkSubmitModalProps {
  /** The homework being handed in, or null when the dialog is closed. */
  homework: StudentWorkItem | null;
  isSubmitting: boolean;
  onClose: () => void;
  onSubmit: (file: File, notes: string) => void;
}

/**
 * Handing homework in: the file, and an optional note to the teacher —
 * same like how we have while submitting the activity.
 */
export function HomeworkSubmitModal({
  homework,
  isSubmitting,
  onClose,
  onSubmit,
}: HomeworkSubmitModalProps) {
  const [file, setFile] = useState<File | null>(null);
  const [notes, setNotes] = useState('');
  const [fileError, setFileError] = useState<string | undefined>();

  // A fresh form for each homework opened.
  useEffect(() => {
    setFile(null);
    setNotes('');
    setFileError(undefined);
  }, [homework?.key]);

  const noteTooLong = notes.trim().length > NOTE_MAX;

  function submit() {
    if (!file) {
      setFileError('Choose a file to hand in');
      return;
    }
    if (noteTooLong) return;
    onSubmit(file, notes.trim());
  }

  return (
    <Modal
      isOpen={Boolean(homework)}
      onClose={onClose}
      title={homework ? `Hand in: ${homework.title}` : ''}
      description={
        homework?.submission
          ? 'Handing in again replaces what you sent before, until your teacher scores it.'
          : 'Your teacher will see the file and your note.'
      }
      size="md"
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={submit}
            isLoading={isSubmitting}
            disabled={noteTooLong}
          >
            Hand in
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <FileUpload
          label="Your work"
          required
          file={file && { name: file.name, size: file.size }}
          onSelect={(chosen) => {
            setFile(chosen);
            setFileError(undefined);
          }}
          accept={HAND_IN_ACCEPT}
          maxSizeMb={HAND_IN_MAX_MB}
          hint={`A PDF or a photo, up to ${HAND_IN_MAX_MB} MB.`}
          error={fileError}
          disabled={isSubmitting}
        />
        <Textarea
          label="Note to your teacher (optional)"
          rows={3}
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
          error={noteTooLong ? `Keep the note under ${NOTE_MAX} characters` : undefined}
          disabled={isSubmitting}
        />
      </div>
    </Modal>
  );
}
